import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {Box, Button, Grid, Typography, useTheme} from "@mui/material";
import {ArrowForward} from "@mui/icons-material";
import {useLanding} from "./LandingLayout";
import {useIsMobile} from "pages/landing";
import LoanCalculator from "./LoanCalculator";

const LandingCarousel = forwardRef(({handleLearnMore}: any, ref) => {
  let sliderRef = useRef<any>(null);
  const [current, setCurrent] = useState(0);
  const [showLoanCalculator, setShowLoanCalculator] = useState(false);

  const theme = useTheme();
  const isMobile = useIsMobile();

  useImperativeHandle(ref, () => ({
    next: () => sliderRef.current.slickNext(),
    previous: () => sliderRef.current.slickPrev(),
    goTo: (index: number) => sliderRef.current.slickGoTo(index),
  }));

  const ApplyNowButton = ({custom = null}) => {
    const {setEarlyAccess} = useLanding();

    const showEarlyAccess = () => {
      setEarlyAccess(true);
    };

    return (
      <Button
        variant="contained"
        sx={{
          height: "32px",
          borderRadius: "8px",
          backgroundColor: "#0053FD",
          color: "white",
          fontSize: "15px",
          padding: "0 16px",
          textTransform: "none",
          boxShadow: "none",
          width: "8.5rem",
        }}
        onClick={() => {
          if (custom) {
            if (custom === "1") {
              // showEarlyAccess();
              window.open(`${process.env.REACT_APP_PORTAL_URL}/sbl`, "_blank");
              return;
            }
            if (custom === "2") {
              // showEarlyAccess();
              window.open(`${process.env.REACT_APP_PORTAL_URL}/rnd`, "_blank");
              return;
            }
          }
          showEarlyAccess();
        }}
      >
        {custom ? (
          <>
            Apply now <ArrowForward sx={{marginLeft: "0.5rem"}} />
          </>
        ) : (
          <>Coming soon</>
        )}
      </Button>
    );
  };

  const PrevArrow = (props) => {
    const {className, style, onClick} = props;
    return (
      <img
        className={className}
        src="/images/arrow-left-carousel.svg"
        style={
          isMobile
            ? {
                ...style,
                padding: "10px 10px",
                boxShadow: "0px 0px 8px 0px #0000001A",
                borderRadius: "8px",
                cursor: "pointer",
                background: "white",
                zIndex: 1,
              }
            : {
                ...style,
                padding: "20px 25px",
                boxShadow: "0px 0px 8px 0px #0000001A",
                borderRadius: "8px",
                cursor: "pointer",
                background: "white",
                left: "-5rem",
                zIndex: 1,
              }
        }
        onClick={onClick}
      />
    );
  };

  const NextArrow = (props) => {
    const {className, style, onClick} = props;
    return (
      <img
        className={className}
        src="/images/arrow-right-carousel.svg"
        style={
          isMobile
            ? {
                ...style,
                padding: "10px 10px",
                boxShadow: "0px 0px 8px 0px #0000001A",
                borderRadius: "8px",
                cursor: "pointer",
                background: "white",
                zIndex: 1,
              }
            : {
                ...style,
                padding: "20px 25px",
                boxShadow: "0px 0px 8px 0px #0000001A",
                borderRadius: "8px",
                cursor: "pointer",
                background: "white",
                right: "-5rem",
                zIndex: 1,
              }
        }
        onClick={onClick}
      />
    );
  };

  const settings = isMobile
    ? {
        prevArrow: <PrevArrow />,
        nextArrow: <NextArrow />,
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
      }
    : {
        prevArrow: <PrevArrow />,
        nextArrow: <NextArrow />,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        variableWidth: true,
        useCSS: true,
        useTransform: true,
        cssEase: "ease-out",
        beforeChange: (_, next) => setCurrent(next),
      };

  return (
    <>
      <Box>
        <Slider ref={sliderRef} {...settings}>
          <Box
            sx={{
              border: "1px solid #E5E5E5",
              borderRadius: "24px",
              padding: "3.125vw",
              width: "auto !important",
              background: "white",
              minHeight: "30rem",
              [theme.breakpoints.down("md")]: {
                minHeight: "100vh",
              },
            }}
          >
            <Grid container spacing={5}>
              <Grid component="div" sx={{textAlign: "left"}}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: "48px",
                    color: "#0A0A0A",
                    lineHeight: 1.2,
                    [theme.breakpoints.down("md")]: {
                      fontSize: "36px",
                    },
                  }}
                  fontWeight={"600"}
                  mt={5}
                >
                  Secured Business Loan
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "20px",
                    marginTop: "1rem",
                    color: "#474747",
                    [theme.breakpoints.down("md")]: {
                      fontSize: "16px",
                    },
                  }}
                  fontWeight={"normal"}
                  lineHeight={"30px"}
                >
                  Flexible, Property-Secured Loans from $300k to $10m+
                </Typography>
                <Box sx={{display: "flex", marginTop: "1rem"}}>
                  <Box
                    sx={{
                      borderRight: "1px solid #E5E5E5",
                      paddingRight: "1rem",
                    }}
                  >
                    <Typography fontSize={13} fontWeight={"normal"}>
                      Amount
                    </Typography>
                    <Typography fontSize={22} fontWeight={"bold"}>
                      $300k - $10m+
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      paddingLeft: "1rem",
                    }}
                  >
                    <Typography fontSize={13} fontWeight={"normal"}>
                      Loan Term
                    </Typography>
                    <Typography fontSize={22} fontWeight={"bold"}>
                      1-24 months
                    </Typography>
                  </Box>
                </Box>
                {/* Checklist */}
                <Box
                  sx={{
                    marginTop: "1rem",
                    padding: "1rem 0",
                    borderTop: "1px solid #E5E5E5",
                    borderBottom: "1px solid #E5E5E5",
                    maxWidth: "95%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      margin: "10px 0",
                    }}
                  >
                    <img src="/images/checklisting.svg" />
                    <Typography fontSize={15} fontWeight={"normal"}>
                      Security: Real estate- Backed (Residential, Commercial,
                      Mixed/Industrial, Land)
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      margin: "10px 0",
                    }}
                  >
                    <img src="/images/checklisting.svg" />
                    <Typography fontSize={15} fontWeight={"normal"}>
                      Interest rates <b>starting at 10% P.A.*</b>
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      margin: "10px 0",
                    }}
                  >
                    <img src="/images/checklisting.svg" />
                    <Typography fontSize={15} fontWeight={"normal"}>
                      Loan to value ratio: Adjustable up to 70%
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      margin: "10px 0",
                    }}
                  >
                    <img src="/images/checklisting.svg" />
                    <Typography fontSize={15} fontWeight={"normal"}>
                      Repayment Options:{" "}
                      <b>Interest-only in advance or Interest Capitalised</b>
                    </Typography>
                  </Box>
                </Box>

                {/* Buttons */}
                <Box sx={{display: "flex", gap: "1rem", margin: "1rem 0"}}>
                  <ApplyNowButton custom={"1"} />
                  <Button
                    variant="contained"
                    sx={{
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: "white",
                      color: "#0A0A0A",
                      fontSize: "15px",
                      padding: "0 16px",
                      textTransform: "none",
                      boxShadow: "none",
                      border: "1px solid #E5E5E5",
                      width: "8.5rem",
                      fontWeight: "600",
                    }}
                    onClick={() => handleLearnMore(1)}
                  >
                    Learn more
                  </Button>
                </Box>

                <Typography fontSize={13} color="#848484">
                  Rate & terms are subject to assessment and change*
                </Typography>
              </Grid>
            </Grid>
          </Box>
          {/* Slide 2 */}
          <Box
            sx={{
              border: "1px solid #E5E5E5",
              borderRadius: "24px",
              padding: "3.125vw",
              width: "auto !important",
              background: "white",
              minHeight: "30rem",
              [theme.breakpoints.down("md")]: {
                minHeight: "100vh",
              },
            }}
          >
            <Grid container spacing={3}>
              <Grid component="div" sx={{textAlign: "left"}}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: "48px",
                    color: "#0A0A0A",
                    lineHeight: 1.2,
                    [theme.breakpoints.down("md")]: {
                      fontSize: "36px",
                    },
                  }}
                  fontWeight={"600"}
                  mt={5}
                >
                  R&D Tax Incentive Financing
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "20px",
                    marginTop: "1rem",
                    color: "#474747",
                    [theme.breakpoints.down("md")]: {
                      fontSize: "16px",
                    },
                  }}
                  fontWeight={"normal"}
                  lineHeight={"30px"}
                >
                  Accelerate Access to Your R&D Tax Refund – Fast and Flexible
                </Typography>
                <Box sx={{display: "flex", marginTop: "1rem"}}>
                  <Box
                    sx={{
                      borderRight: "1px solid #E5E5E5",
                      paddingRight: "1rem",
                    }}
                  >
                    <Typography fontSize={13} fontWeight={"normal"}>
                      Amount
                    </Typography>
                    <Typography fontSize={22} fontWeight={"bold"}>
                      $100k - $2m+
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      paddingLeft: "1rem",
                    }}
                  >
                    <Typography fontSize={13} fontWeight={"normal"}>
                      Loan Term
                    </Typography>
                    <Typography fontSize={22} fontWeight={"bold"}>
                      3-12 months
                    </Typography>
                  </Box>
                </Box>
                {/* Checklist */}
                <Box
                  sx={{
                    marginTop: "1rem",
                    padding: "1rem 0",
                    borderTop: "1px solid #E5E5E5",
                    borderBottom: "1px solid #E5E5E5",
                    maxWidth: "95%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      margin: "10px 0",
                    }}
                  >
                    <img src="/images/checklisting.svg" />
                    <Typography fontSize={15} fontWeight={"normal"}>
                      Security:{" "}
                      <b>Registered claim against R&D Tax Incentive refund</b>
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      margin: "10px 0",
                    }}
                  >
                    <img src="/images/checklisting.svg" />
                    <Typography fontSize={15} fontWeight={"normal"}>
                      Interest rates <b>starting at 16% P.A.*</b>
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      margin: "10px 0",
                    }}
                  >
                    <img src="/images/checklisting.svg" />
                    <Typography fontSize={15} fontWeight={"normal"}>
                      Advance Rate: Up to 80% of your verified R&D claim
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      margin: "10px 0",
                    }}
                  >
                    <img src="/images/checklisting.svg" />
                    <Typography fontSize={15} fontWeight={"normal"}>
                      Repayment:{" "}
                      <b>Interest-only in advance or Interest Capitalised</b>
                    </Typography>
                  </Box>
                </Box>
                {/* Buttons */}
                <Box sx={{display: "flex", gap: "1rem", margin: "1rem 0"}}>
                  <ApplyNowButton custom={"2"} />

                  <Button
                    variant="contained"
                    sx={{
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: "#0053FD",
                      color: "white",
                      fontSize: "15px",
                      padding: "0 16px",
                      textTransform: "none",
                      boxShadow: "none",
                      width: "8.5rem",
                    }}
                    onClick={(e) => {
                      setShowLoanCalculator(true);
                    }}
                  >
                    Calculate
                    {/* <Box
                    component="img"
                    src="/images/icon-arrow-right.svg"
                    sx={{marginLeft: "1rem"}}
                  /> */}
                  </Button>

                  <Button
                    variant="contained"
                    sx={{
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: "white",
                      color: "#0A0A0A",
                      fontSize: "15px",
                      padding: "0 16px",
                      textTransform: "none",
                      boxShadow: "none",
                      border: "1px solid #E5E5E5",
                      width: "8.5rem",
                      fontWeight: "bold",
                    }}
                    onClick={() => handleLearnMore(2)}
                  >
                    Learn more
                  </Button>
                </Box>
                <Typography fontSize={13} color="#848484">
                  Rate & terms are subject to assessment and change*
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Slider>
      </Box>
      {showLoanCalculator && (
        <LoanCalculator
          showForm={showLoanCalculator}
          onCloseForm={() => setShowLoanCalculator(false)}
        />
      )}
    </>
  );
});

export default LandingCarousel;
